import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Send, 
  Loader2, 
  Shield,
  Phone,
  Video,
  MoreVertical,
  CheckCheck,
  Clock,
  Heart,
  Info
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/vivah`;

export default function VivahChatPage({ user }) {
  const { interestId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [partner, setPartner] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (interestId) {
      fetchMessages();
      fetchPartnerInfo();
      
      // Poll for new messages every 5 seconds
      const interval = setInterval(fetchMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [interestId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/chat/${interestId}/messages?token=${token}`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerInfo = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/interests/accepted?token=${token}`);
      const match = response.data.matches?.find(m => m.interest_id === interestId);
      if (match) {
        setPartner(match.profile);
      }
    } catch (error) {
      console.error('Failed to fetch partner info:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || sending) return;

    const messageText = inputValue.trim();
    setInputValue('');
    setSending(true);

    // Optimistic update
    const tempMessage = {
      id: `temp-${Date.now()}`,
      text: messageText,
      is_own: true,
      is_read: false,
      created_at: new Date().toISOString(),
      sending: true
    };
    setMessages(prev => [...prev, tempMessage]);

    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(
        `${API}/chat/${interestId}/message?text=${encodeURIComponent(messageText)}&token=${token}`
      );
      
      // Refresh messages
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
      setInputValue(messageText);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString([], { day: 'numeric', month: 'short' });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.created_at);
    if (!groups[date]) groups[date] = [];
    groups[date].push(message);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-rose-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-rose-50 via-white to-amber-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button 
          onClick={() => navigate('/vivah/chat')}
          className="p-2 hover:bg-gray-100 rounded-full"
          data-testid="chat-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        {partner && (
          <div 
            className="flex items-center gap-3 flex-1 cursor-pointer"
            onClick={() => navigate(`/vivah/profile/${partner.id}`)}
          >
            <div className="relative">
              {partner.profile_photo ? (
                <img 
                  src={partner.profile_photo} 
                  alt={partner.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                  <span className="text-rose-600 font-bold">{partner.name?.charAt(0)}</span>
                </div>
              )}
              {partner.verification_level >= 2 && (
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                  <Shield className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-gray-800 truncate">{partner.name}</h2>
              <p className="text-xs text-gray-500 truncate">{partner.profile_id}</p>
            </div>
          </div>
        )}

        <button className="p-2 hover:bg-gray-100 rounded-full">
          <MoreVertical className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {/* Match Banner */}
        <div className="flex justify-center mb-6">
          <div className="bg-gradient-to-r from-rose-100 to-amber-100 px-4 py-2 rounded-full flex items-center gap-2">
            <Heart className="w-4 h-4 text-rose-500" fill="#f43f5e" />
            <span className="text-sm text-rose-700">You matched! Start a conversation</span>
          </div>
        </div>

        {/* Messages grouped by date */}
        {Object.entries(groupedMessages).map(([date, dateMessages]) => (
          <div key={date}>
            <div className="flex justify-center my-4">
              <span className="text-xs text-gray-500 bg-white px-3 py-1 rounded-full shadow-sm">
                {date}
              </span>
            </div>

            {dateMessages.map((message, index) => (
              <div
                key={message.id}
                className={`flex mb-3 ${message.is_own ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[75%] px-4 py-2 rounded-2xl ${
                    message.is_own
                      ? 'bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-br-md'
                      : 'bg-white shadow-md text-gray-800 rounded-bl-md'
                  }`}
                >
                  <p className="text-sm leading-relaxed">{message.text}</p>
                  <div className={`flex items-center gap-1 justify-end mt-1 ${
                    message.is_own ? 'text-rose-200' : 'text-gray-400'
                  }`}>
                    <span className="text-xs">{formatTime(message.created_at)}</span>
                    {message.is_own && (
                      message.sending ? (
                        <Clock className="w-3 h-3" />
                      ) : (
                        <CheckCheck className={`w-3 h-3 ${message.is_read ? 'text-white' : ''}`} />
                      )
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}

        {messages.length === 0 && (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Send className="w-8 h-8 text-rose-400" />
            </div>
            <p className="text-gray-600 mb-2">No messages yet</p>
            <p className="text-sm text-gray-400">Say hello to start the conversation!</p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="bg-white border-t px-4 py-3 safe-area-bottom">
        <form onSubmit={sendMessage} className="flex items-center gap-3">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-4 py-3 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-rose-500"
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || sending}
            className="w-12 h-12 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-full flex items-center justify-center disabled:opacity-50 hover:shadow-lg transition-shadow"
            data-testid="chat-send-btn"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
